import { memo, useCallback, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { WorkspaceAgentPanelZeroStateSuggestionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/WorkspaceAgentPanelZeroStateSuggestionRow';
import {
  WORKSPACE_AGENT_IMPROVED_NULL_STATE_PROMPT,
  WORKSPACE_AGENT_PROMPT_CARDS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentImprovedNullState';
import {
  WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO_WITH_SELECTION,
  type WorkspaceAgentPanelZeroStateChip,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import {
  WorkspaceAgentPromptCardCarousel,
  type WorkspaceAgentPromptCardCarouselItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/carousel/WorkspaceAgentPromptCardCarousel';
import { WorkspaceAgentPromptCardCarouselSkeleton } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/carousel/WorkspaceAgentPromptCardCarouselSkeleton';
import { type WorkspaceAgentPromptCardThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/WorkspaceAgentPromptCardImageStack';
import { usePrivateWorkspaceAgentGuidedVariantFlowTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentGuidedVariantFlowTracking';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { usePrivateWorkspaceAgentCdnLinksPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentCdnLinksPromptCard';
import { usePrivateWorkspaceAgentFindDuplicatesPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentFindDuplicatesPromptCard';
import { usePrivateWorkspaceAgentNewUploadsPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentNewUploadsPromptCard';
import { usePrivateWorkspaceAgentResizeForSocialPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentResizeForSocialPromptCard';
import { usePrivateWorkspaceAgentUntaggedAssetsPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentUntaggedAssetsPromptCard';
import { usePrivateWorkspaceAgentUnusedAssetsPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentUnusedAssetsPromptCard';
import { usePrivateWorkspaceAgentUpdateCustomFieldsPromptCard } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/workspace-agent/usePrivateWorkspaceAgentUpdateCustomFieldsPromptCard';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { useAccount } from '~/swr-hooks/account/useAccount';

const RESIZE_FOR_SOCIAL_PROMPT_CARD_ID = 'prompt-card-resize-board';

type WorkspaceAgentPromptCardDefinition = (typeof WORKSPACE_AGENT_PROMPT_CARDS)[number];

const toStaticPromptCardThumbnails = (thumbnailUrls: readonly string[]) =>
  thumbnailUrls.map((thumbnailUrl) => ({ thumbnailUrl }));

const toCarouselCard = (
  card: Pick<WorkspaceAgentPromptCardDefinition, 'buttonLabel' | 'id' | 'title'>,
  thumbnails: readonly WorkspaceAgentPromptCardThumbnail[],
  subtitle: string,
): WorkspaceAgentPromptCardCarouselItem => ({
  buttonLabel: card.buttonLabel,
  id: card.id,
  subtitle,
  thumbnails,
  title: card.title,
});

export type PrivateWorkspaceAgentImprovedNullStateProps = {
  hasExplicitSelection: boolean;
  isVisible: boolean;
  onSuggestionSelect: (id: string) => void;
  showQuickReplyShimmer: boolean;
  suggestions: readonly WorkspaceAgentPanelZeroStateChip[];
  transitionDuration: number;
};

export const PrivateWorkspaceAgentImprovedNullState = memo(
  ({
    hasExplicitSelection,
    isVisible,
    onSuggestionSelect,
    showQuickReplyShimmer,
    suggestions,
    transitionDuration,
  }: PrivateWorkspaceAgentImprovedNullStateProps) => {
    const { data: account } = useAccount();
    const { sessionId, sessions } = usePrivateWorkspaceAgentPanelSession();
    const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
    const { seedComposer } = useWorkspaceAgentComposerSeed();
    const { isPending: isCdnLinksPending, isVisible: isCdnLinksVisible } = usePrivateWorkspaceAgentCdnLinksPromptCard();
    const { isPending: isFindDuplicatesPending, title: findDuplicatesTitle } =
      usePrivateWorkspaceAgentFindDuplicatesPromptCard();

    const {
      isPending: isNewUploadsPending,
      thumbnails: newUploadsThumbnails,
      total: newUploadsTotal,
    } = usePrivateWorkspaceAgentNewUploadsPromptCard();

    const {
      boardName: untaggedAssetsBoardName,
      isPending: isUntaggedAssetsPending,
      thumbnails: untaggedAssetsThumbnails,
      total: untaggedAssetsTotal,
    } = usePrivateWorkspaceAgentUntaggedAssetsPromptCard();

    const {
      isPending: isUnusedAssetsPending,
      staleSinceYear: unusedAssetsStaleSinceYear,
      thumbnails: unusedAssetsThumbnails,
      total: unusedAssetsTotal,
    } = usePrivateWorkspaceAgentUnusedAssetsPromptCard();

    const { isPending: isResizeForSocialPending, thumbnails: resizeForSocialThumbnails } =
      usePrivateWorkspaceAgentResizeForSocialPromptCard();
    const { fieldName: updateCustomFieldsFieldName, isPending: isUpdateCustomFieldsPending } =
      usePrivateWorkspaceAgentUpdateCustomFieldsPromptCard();

    const openGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.openGuidedVariantFlow);
    const { trackGuidedVariantFlowOpened } = usePrivateWorkspaceAgentGuidedVariantFlowTracking({ chatId: sessionId });

    const isPromptCardDataPending =
      isCdnLinksPending ||
      isFindDuplicatesPending ||
      isNewUploadsPending ||
      isResizeForSocialPending ||
      isUntaggedAssetsPending ||
      isUnusedAssetsPending ||
      isUpdateCustomFieldsPending;

    const cards = useMemo(
      () =>
        WORKSPACE_AGENT_PROMPT_CARDS.flatMap((card) => {
          switch (card.id) {
            case 'prompt-card-export-cdn-links':
              return isCdnLinksVisible
                ? [toCarouselCard(card, toStaticPromptCardThumbnails(card.thumbnailUrls), card.getSubtitle())]
                : [];
            case 'prompt-card-find-duplicates':
              return [
                toCarouselCard(
                  { ...card, title: findDuplicatesTitle },
                  toStaticPromptCardThumbnails(card.thumbnailUrls),
                  card.getSubtitle(),
                ),
              ];
            case 'prompt-card-tag-new-uploads':
              return newUploadsTotal > 0
                ? [toCarouselCard(card, newUploadsThumbnails, card.getSubtitle(newUploadsTotal))]
                : [];
            case 'prompt-card-tag-product-shots':
              return untaggedAssetsTotal > 0
                ? [
                    toCarouselCard(
                      card,
                      untaggedAssetsThumbnails,
                      card.getSubtitle(untaggedAssetsTotal, { boardName: untaggedAssetsBoardName }),
                    ),
                  ]
                : [];
            case 'prompt-card-unused-assets':
              return unusedAssetsTotal > 0
                ? [
                    toCarouselCard(
                      card,
                      unusedAssetsThumbnails,
                      card.getSubtitle(unusedAssetsTotal, { staleSinceYear: unusedAssetsStaleSinceYear }),
                    ),
                  ]
                : [];
            case 'prompt-card-update-custom-fields':
              return [
                toCarouselCard(
                  card,
                  toStaticPromptCardThumbnails(card.thumbnailUrls),
                  `Missing a ${updateCustomFieldsFieldName}`,
                ),
              ];
            case 'prompt-card-resize-board': {
              const fallbackThumbnails = toStaticPromptCardThumbnails(card.thumbnailUrls);

              return [
                toCarouselCard(
                  card,
                  resizeForSocialThumbnails.length > 0 ? resizeForSocialThumbnails : fallbackThumbnails,
                  card.getSubtitle(),
                ),
              ];
            }
            default:
              return [toCarouselCard(card, toStaticPromptCardThumbnails(card.thumbnailUrls), card.getSubtitle())];
          }
        }),
      [
        findDuplicatesTitle,
        isCdnLinksVisible,
        untaggedAssetsBoardName,
        newUploadsThumbnails,
        newUploadsTotal,
        resizeForSocialThumbnails,
        updateCustomFieldsFieldName,
        untaggedAssetsThumbnails,
        untaggedAssetsTotal,
        unusedAssetsStaleSinceYear,
        unusedAssetsThumbnails,
        unusedAssetsTotal,
      ],
    );

    const firstName = account?.firstName;
    const isReturning = sessions.length > 0;
    const greeting = useMemo(() => {
      if (hasExplicitSelection) {
        return WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO_WITH_SELECTION;
      }

      if (isReturning) {
        return firstName ? `Hey, ${firstName}, welcome back!` : 'Welcome back!';
      }

      return firstName ? `What should we work on, ${firstName}?` : 'What should we work on?';
    }, [firstName, hasExplicitSelection, isReturning]);

    const handleCardSelect = useCallback(
      (cardId: string) => {
        if (!cards.some((candidate) => candidate.id === cardId) || blockIfCreditsDepleted()) {
          return;
        }

        if (cardId === RESIZE_FOR_SOCIAL_PROMPT_CARD_ID) {
          trackGuidedVariantFlowOpened({ entry: 'zero_state' });
          openGuidedVariantFlow('smart-resize');
          return;
        }

        seedComposer(WORKSPACE_AGENT_IMPROVED_NULL_STATE_PROMPT, {
          sendImmediately: true,
          source: 'hero_chip',
        });
      },
      [blockIfCreditsDepleted, cards, openGuidedVariantFlow, seedComposer, trackGuidedVariantFlowOpened],
    );

    const promptCardRow = useMemo(() => {
      if (isPromptCardDataPending) {
        return <WorkspaceAgentPromptCardCarouselSkeleton />;
      }

      if (cards.length === 0) {
        return null;
      }

      return (
        <WorkspaceAgentPromptCardCarousel cards={cards} isDisabled={isCreditsDepleted} onSelect={handleCardSelect} />
      );
    }, [cards, handleCardSelect, isCreditsDepleted, isPromptCardDataPending]);

    return (
      <div
        className="mt-auto flex w-full min-w-0 max-w-full flex-col gap-4 pt-5 pb-3"
        data-testid="WORKSPACE_AGENT_PANEL_ZERO_STATE"
      >
        <p
          className={cn(
            'text-14 leading-5',
            isCreditsDepleted ? 'ads:text-secondary text-gray-11' : 'ads:text-primary text-gray-12',
          )}
          data-testid="WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO"
        >
          {greeting}
        </p>
        {promptCardRow}
        <div className="flex w-full min-w-0 flex-col items-start gap-2 overflow-visible py-0.5">
          {suggestions.map((suggestion, index) => (
            <WorkspaceAgentPanelZeroStateSuggestionRow
              index={index}
              isDisabled={isCreditsDepleted}
              isVisible={isVisible}
              key={suggestion.id}
              onSelect={onSuggestionSelect}
              showQuickReplyShimmer={showQuickReplyShimmer}
              slotCount={suggestions.length}
              suggestion={suggestion}
              transitionDuration={transitionDuration}
              variant="ghost"
            />
          ))}
        </div>
      </div>
    );
  },
);

PrivateWorkspaceAgentImprovedNullState.displayName = 'PrivateWorkspaceAgentImprovedNullState';
